import { useMemo } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { Download } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { PeriodOption } from '../types'

interface BillingHeaderProps {
  orgLabel: string
  period: string
  periodOptions: PeriodOption[]
  onPeriodChange: (value: string) => void
  twilioPulledAt: Date | null
  isCurrent: boolean
}

const STALE_HOURS_WARN = 6
const STALE_HOURS_BAD = 24

function stalenessTone(pulledAt: Date | null): 'fresh' | 'warn' | 'bad' {
  if (!pulledAt) return 'bad'
  const ageHours = (Date.now() - pulledAt.getTime()) / (60 * 60 * 1000)
  if (ageHours < STALE_HOURS_WARN) return 'fresh'
  if (ageHours < STALE_HOURS_BAD) return 'warn'
  return 'bad'
}

export function BillingHeader({
  orgLabel,
  period,
  periodOptions,
  onPeriodChange,
  twilioPulledAt,
  isCurrent,
}: BillingHeaderProps) {
  const periodLabel = useMemo(
    () => periodOptions.find((o) => o.value === period)?.label ?? period,
    [periodOptions, period]
  )
  const tone = useMemo(() => stalenessTone(twilioPulledAt), [twilioPulledAt])
  const stalenessLabel = useMemo(() => {
    if (!twilioPulledAt) return 'Data unavailable'
    const verb = isCurrent ? 'updated' : 'finalized'
    return `Data ${verb} ${formatDistanceToNow(twilioPulledAt, { addSuffix: true })}`
  }, [twilioPulledAt, isCurrent])

  // The browser names the print-to-PDF from document.title (otherwise just
  // "Careforce"). Set a descriptive name for the duration of the print, then
  // restore it — e.g. "Careforce - Bright Smile Dental - July 2026 - Invoice".
  const handleDownloadPdf = () => {
    const month = periodLabel.replace(/\s*\(current\)$/i, '').trim()
    const name = `Careforce - ${orgLabel} - ${month} - Invoice`.replace(
      /[\\/:*?"<>|]/g,
      '-'
    )
    const previousTitle = document.title
    document.title = name
    const restore = () => {
      document.title = previousTitle
      window.removeEventListener('afterprint', restore)
    }
    window.addEventListener('afterprint', restore)
    window.print()
  }

  return (
    <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Billing</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {orgLabel} · Read-only
        </p>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-xs text-muted-foreground flex items-center gap-1.5">
          <span
            className={cn(
              'w-2 h-2 rounded-full',
              tone === 'fresh' && 'bg-green-500',
              tone === 'warn' && 'bg-amber-500',
              tone === 'bad' && 'bg-red-500'
            )}
          />
          {stalenessLabel}
        </div>

        <div className="hidden-on-print">
          <Select value={period} onValueChange={onPeriodChange}>
            <SelectTrigger size="sm" className="min-w-44">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              {periodOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* On the printed receipt the dropdown reads as an interactive control;
            show the selected period as plain text instead. */}
        <span className="print-only text-sm font-medium text-foreground">
          {periodLabel}
        </span>

        <Button
          size="xs"
          onClick={handleDownloadPdf}
          className="hidden-on-print">
          <Download />
          Download PDF
        </Button>
      </div>
    </div>
  )
}
